import { useSetAtom } from "jotai";

import { useEffect } from "react";

import { authBootstrapStateAtom, currentUserAtom } from "../atoms/auth.atoms";

import authService from "../services/authService";

import { syncSessionWithExtension } from "../lib/syncSessionWithExtension";

/**
 * Runs once on app mount to determine whether the user is logged in.
 *
 * Works in both contexts:
 *   - Browser (app.eriol.io): the HttpOnly cookie set by /auth/login rides
 *     along on /me via credentials:include. 200 → authenticated.
 *   - Electron: the Authorization header is added by lib/authFetch from
 *     localStorage. Same /me path.
 *
 * A 401 just means "no session" — we transition to "unauthenticated" and
 * let route guards send the user to /login. Anything else (network error,
 * 5xx) also lands in "unauthenticated" since we have no proof of session;
 * the route guard handles that the same way (kicks to /login).
 *
 * The `auth-changed` window event (dispatched by Login/Signup hooks) lets
 * us re-bootstrap after a fresh login without a full page reload.
 */

export function useAuthBootstrap() {
  const setState = useSetAtom(authBootstrapStateAtom);

  const setUser = useSetAtom(currentUserAtom);

  useEffect(() => {
    let active = true;
    let inFlight = false;
    let sessionVersion = 0;

    const run = async () => {
      if (!active || inFlight) return;

      inFlight = true;
      const requestedVersion = sessionVersion;
      const isCurrent = () => active && requestedVersion === sessionVersion;

      try {
        const me = await authService.getMe();
        if (!isCurrent()) return;

        // Re-afirmar el scope per-user en la extensión SIEMPRE que el bootstrap

        // considere autenticado (cookie o header), independientemente del carril

        // de re-login. Sin esto, un logout→login sin reiniciar la app que se

        // resuelve por cookie deja `currentUserId` en null en la extensión →

        // los snapshots per-user dan 204 → la app re-pide la escena (Welcome)

        // al seleccionar un workspace ya configurado. Idempotente / no-op sin

        // sesión Electron.

        await syncSessionWithExtension();
        if (!isCurrent()) return;

        setUser(me);
        setState("authenticated");
      } catch {
        if (!isCurrent()) return;

        setUser(null);
        setState("unauthenticated");
      } finally {
        inFlight = false;
        // Desktop hydration or login can change credentials while /me is
        // pending. Discard that response and read the latest session instead
        // of letting an old, incomplete profile trigger AccountSetup.
        if (active && requestedVersion !== sessionVersion) void run();
      }
    };

    void run();

    const onAuthChanged = () => {
      // Reset to checking so guards show their spinner while we refetch.

      sessionVersion++;
      setUser(null);
      setState("checking");

      void run();
    };

    window.addEventListener("auth-changed", onAuthChanged);

    return () => {
      active = false;
      window.removeEventListener("auth-changed", onAuthChanged);
    };
  }, [setState, setUser]);
}
